// src/components/ui/ClosedBanner.jsx
import React from 'react';
import styled from '@emotion/styled';
import { CalendarClock, Clock, Moon, Sparkle } from 'lucide-react';
import { useContent } from 'context/ContentContext';

/**
 * ClosedBanner Component
 *
 * Displays a banner when the restaurant is closed or not taking online orders
 *
 * @param {boolean} isClosed - Whether the restaurant is currently closed
 * @param {Object} nextOpening - Next opening time {day, time, formatted}
 * @param {string|null} businessHoursOverride - Override state ('closed' = force closed)
 * @param {boolean} isGracePeriod - Whether we're in grace period (open but not taking orders)
 * @param {boolean} shoppingEnabled - Whether the online ordering feature is enabled at all
 * @param {boolean} orderingEnabled - Whether anything (ASAP or scheduled) can be ordered right now (#485)
 * @param {Object} businessHours - Business hours data
 */
const ClosedBanner = ({
  isClosed,
  nextOpening,
  businessHoursOverride,
  isGracePeriod,
  shoppingEnabled,
  orderingEnabled,
  businessHours,
}) => {
  const { messages } = useContent();
  // Hide banner if the ordering feature is disabled entirely, or no business hours data
  if (!shoppingEnabled || !businessHours) return null;

  // Hide banner if not closed
  if (!isClosed) return null;

  // Determine the scenario
  const isForceClosedOnly = businessHoursOverride === 'closed';
  // orderingEnabled is false under an indefinite force close, and true under
  // a timed one, where pickups after it ends can still be booked (#668).
  const showScheduledOrdering = orderingEnabled && !isGracePeriod;

  const handleScrollToLocation = () => {
    const locationSection = document.getElementById('location');
    if (locationSection) {
      locationSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Get translated messages with fallbacks
  const closedMessages = messages?.closedBanner || {};
  const forceClosed =
    closedMessages.forceClosed ||
    "We're not currently taking online orders. Stop by or give us a call";
  const gracePeriod =
    closedMessages.gracePeriod ||
    "We're closing soon and no longer taking online orders";
  const gracePeriodWithTime =
    closedMessages.gracePeriodWithTime ||
    "We're closing soon and no longer taking online orders. We open again";
  const closed = closedMessages.closed || "We're currently closed";
  const closedWithTime =
    closedMessages.closedWithTime || "We're currently closed. We'll open";
  const viewHours = closedMessages.viewHours || 'View hours & location';
  const closedNowLabel = closedMessages.closedNowLabel || 'Closed Now';
  const scheduledAvailable =
    closedMessages.scheduledAvailable || 'Scheduled ordering is available.';
  const scheduledAvailableDetail =
    closedMessages.scheduledAvailableDetail ||
    'Add items to your cart and choose a pickup time at checkout.';
  const opensLabel = closedMessages.opensLabel || 'Opens';

  // Scenario: closed (by the schedule or a timed force close), but scheduled
  // ordering is still bookable — lead with that instead of a plain closure.
  if (showScheduledOrdering) {
    return (
      <ScheduledCard role="alert">
        <ScheduleIcon>
          <CalendarClock size={40} strokeWidth={1.75} aria-hidden="true" />
        </ScheduleIcon>
        <MessageBlock>
          <Headline>{scheduledAvailable}</Headline>
          <Detail>{scheduledAvailableDetail}</Detail>
        </MessageBlock>
        <Divider aria-hidden="true" />
        <StatusButton
          type="button"
          onClick={handleScrollToLocation}
          title={viewHours}
        >
          <MoonMark aria-hidden="true">
            <Moon size={38} strokeWidth={0} fill="currentColor" />
            <Sparkle size={12} strokeWidth={0} fill="currentColor" />
          </MoonMark>
          <StatusText>
            <StatusLabel>{closedNowLabel}</StatusLabel>
            {nextOpening && (
              <StatusCaption>
                {opensLabel} {nextOpening.formatted}
              </StatusCaption>
            )}
            <VisuallyHidden>{viewHours}</VisuallyHidden>
          </StatusText>
        </StatusButton>
      </ScheduledCard>
    );
  }

  // Determine message based on scenario
  let message;
  let showHoursLink = false;

  if (isForceClosedOnly) {
    // Force closed by admin, no scheduling to offer
    message = forceClosed;
  } else if (isGracePeriod) {
    // Grace period (open but stopping orders)
    message = nextOpening
      ? `${gracePeriodWithTime} ${nextOpening.formatted}`
      : gracePeriod;
  } else {
    // Actually closed (outside business hours), no scheduling to offer
    message = nextOpening
      ? `${closedWithTime} ${nextOpening.formatted}`
      : closed;
    showHoursLink = true;
  }

  return (
    <BannerContainer role="alert">
      <Content>
        <OpeningTime>
          <Clock size={18} />
          <span>{message}</span>
        </OpeningTime>
        {showHoursLink && (
          <HoursLink onClick={handleScrollToLocation}>{viewHours}</HoursLink>
        )}
      </Content>
    </BannerContainer>
  );
};

// Styled components
const BannerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 20px;
  margin: 0 0 24px 0;
  background: ${({ theme }) => theme.colors.offWhite};
  border-left: 4px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  ${({ theme }) => theme.mediaQueries.md} {
    padding: 20px 32px;
    margin: 0 0 32px 0;
  }
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
  width: 100%;
  max-width: 800px;

  @media (max-width: 639px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const OpeningTime = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  color: ${({ theme }) => theme.colors.black};
  font-weight: 500;
  line-height: 1.5;

  @media (max-width: 639px) {
    text-align: center;
    justify-content: center;
  }

  svg {
    color: ${({ theme }) => theme.colors.secondary};
    flex-shrink: 0;
    width: 20px;
    height: 20px;
  }
`;

const ScheduledCard = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 18px 24px;
  margin: 0 0 24px 0;
  background: rgba(250, 245, 237, 0.94);
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(58, 42, 30, 0.08);
  text-align: left;

  ${({ theme }) => theme.mediaQueries.md} {
    gap: 28px;
    padding: 24px 36px;
    margin: 0 0 32px 0;
  }

  @media (max-width: 639px) {
    flex-wrap: wrap;
    gap: 14px;
    padding: 16px 18px;
  }
`;

const ScheduleIcon = styled.div`
  display: flex;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.tertiary};

  @media (max-width: 639px) {
    svg {
      width: 32px;
      height: 32px;
    }
  }
`;

const MessageBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;

  @media (max-width: 639px) {
    flex-basis: calc(100% - 46px);
  }
`;

const Headline = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.tertiary};
  line-height: 1.3;

  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 21px;
  }
`;

const Detail = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.darkGrey};
  line-height: 1.45;

  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 15px;
  }
`;

const Divider = styled.span`
  align-self: stretch;
  flex-shrink: 0;
  width: 2px;
  margin: 4px 0;
  border-radius: 1px;
  background: ${({ theme }) => theme.colors.secondaryDark};

  @media (max-width: 639px) {
    flex-basis: 100%;
    width: auto;
    height: 1px;
    margin: 0;
  }
`;

const StatusButton = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
  margin: -6px -8px;
  padding: 6px 8px;
  font: inherit;
  background: none;
  border: none;
  border-radius: 10px;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(199, 59, 44, 0.06);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const MoonMark = styled.span`
  position: relative;
  display: flex;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.primary};

  svg + svg {
    position: absolute;
    top: 1px;
    right: -1px;
    color: ${({ theme }) => theme.colors.secondaryDark};
  }
`;

const StatusText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatusLabel = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 17px;
  font-weight: 800;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const StatusCaption = styled.span`
  color: ${({ theme }) => theme.colors.darkGrey};
  font-size: 14px;
  white-space: nowrap;
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const HoursLink = styled.button`
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  color: ${({ theme }) => theme.colors.black};
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 6px;
  transition: all 0.2s ease;
  position: relative;
  white-space: nowrap;
  flex-shrink: 0;

  &::after {
    content: ' →';
    margin-left: 4px;
    transition: transform 0.2s ease;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.secondaryDark};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(247, 198, 63, 0.3);
  }

  &:hover::after {
    transform: translateX(3px);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.secondaryDark};
    outline-offset: 2px;
  }
`;

export default ClosedBanner;
